import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  BellIcon,
  CaretDownIcon,
  CatIcon,
  CheckIcon,
  DogIcon,
  HeartIcon,
  HouseIcon,
  InfoIcon,
  ListIcon,
  PawPrintIcon,
  PlusIcon,
  QuestionIcon,
  UserCircleIcon,
} from "@phosphor-icons/react";
import {
  COLOR_PELAJE,
  SEXO_MASCOTA,
  TAMANO_MASCOTA,
  TIPO_MASCOTA,
} from "@encuentrayayuda/shared/schemas/mascotaOpciones";
import { crearMascotaSchema } from "@encuentrayayuda/shared/schemas/mascota";
import { sanitizarEmail, sanitizarTexto } from "@encuentrayayuda/shared/lib/sanitizar";
import { hoyLocal } from "@encuentrayayuda/shared/lib/fechas";
import ModalCamposFaltantes from "./ModalCamposFaltantes.jsx";
import AvisoBorrador from "./AvisoBorrador.jsx";
import UbicacionMascotaForm from "./UbicacionMascotaForm.jsx";
import FotosMascotaForm from "./FotosMascotaForm.jsx";
import ConfirmarMascotaForm from "./ConfirmarMascotaForm.jsx";
import Turnstile from "./Turnstile.jsx";
import { publicarMascota } from "./lib/publicarMascota.js";
import ReportePublicado from "./ReportePublicado.jsx";
import { guardarBorrador, hayBorrador, leerBorrador, limpiarBorrador } from "./lib/borradores.js";

const STORAGE_KEY = "encuentrayayuda:mascota-perdida:v2";
const STORAGE_KEYS_ANTERIORES = ["encuentrayayuda:mascota-perdida:informacion"];
const FECHA_INICIAL = "2026-08-10";

const pasos = ["Información y características", "¿Dónde y cuándo?", "Fotos", "Confirmar"];

function clasePaso(numero, pasoActual, maximoPasoAlcanzado) {
  if (numero === pasoActual) return "is-active";
  if (numero < pasoActual) return "is-complete";
  if (numero <= maximoPasoAlcanzado) return "is-available";
  return "";
}

const etiquetasTipo = {
  PERRO: "Perro",
  GATO: "Gato",
  OTRO: "Otra mascota",
};

const iconosTipo = {
  PERRO: DogIcon,
  GATO: CatIcon,
  OTRO: PawPrintIcon,
};

const etiquetasSexo = { MACHO: "Macho", HEMBRA: "Hembra" };

const etiquetasColor = {
  NEGRO: "Negro", BLANCO: "Blanco", MARRON: "Marrón", CAFE_CLARO: "Café claro",
  BEIGE: "Beige", CREMA: "Crema", DORADO: "Dorado", AMARILLO: "Amarillo",
  ROJIZO: "Rojizo", GRIS: "Gris", CANELA: "Canela", ATIGRADO: "Atigrado",
  MANCHADO: "Manchado",
};

const detallesTamano = {
  PERRO: {
    MUY_PEQUENO: ["Muy pequeño / Toy", "0–5 kg"], PEQUENO: ["Pequeño", "5–10 kg"],
    MEDIANO: ["Mediano", "10–25 kg"], GRANDE: ["Grande", "25–40 kg"],
    MUY_GRANDE: ["Muy grande / Gigante", "+40 kg"],
  },
  GATO: {
    MUY_PEQUENO: ["Muy pequeño", "Menos de 3 kg"], PEQUENO: ["Pequeño", "3–4 kg"],
    MEDIANO: ["Mediano", "4–5 kg"], GRANDE: ["Grande", "5–7 kg"],
    MUY_GRANDE: ["Muy grande", "+7 kg"],
  },
  OTRO: {
    MUY_PEQUENO: ["Muy pequeño", ""], PEQUENO: ["Pequeño", ""],
    MEDIANO: ["Mediano", ""], GRANDE: ["Grande", ""],
    MUY_GRANDE: ["Muy grande", ""],
  },
};

const datosIniciales = {
  tipoMascota: "",
  nombreMascota: "",
  sexo: "",
  tamano: "",
  colorPrincipalPelaje: "",
  caracteristicasParticulares: "",
  latPerdido: null,
  lngPerdido: null,
  precisionGps: null,
  direccionHallazgo: "",
  // Los rellena el geocodificador al marcar el punto; si el usuario
  // escribe la direccion a mano quedan null y la tarjeta va sin ubicacion.
  ciudad: null,
  departamento: null,
  puntoReferencia: "",
  fechaMaxima: hoyLocal(),
  fechaPerdida: FECHA_INICIAL,
  horaPerdida: "07:45",
};

function cargarBorrador() {
  try {
    const guardado = leerBorrador(STORAGE_KEY, STORAGE_KEYS_ANTERIORES);
    if (!guardado) return datosIniciales;
    const borrador = { ...datosIniciales, ...JSON.parse(guardado) };
    const hoy = hoyLocal();
    return {
      ...borrador,
      fechaMaxima: hoy,
      fechaPerdida: borrador.fechaPerdida > hoy ? hoy : borrador.fechaPerdida,
    };
  } catch {
    return datosIniciales;
  }
}

function construirPayload(datos, contacto) {
  const payload = {
    tipo: "DESAPARECIDO",
    reportante: {
      nombreReportante: sanitizarTexto(contacto.nombre),
      telefonoReportante: `${contacto.codigoPais}${contacto.telefono}`,
      emailReportante: sanitizarEmail(contacto.email),
    },
    nombreMascota: sanitizarTexto(datos.nombreMascota),
    tipoMascota: datos.tipoMascota,
    sexo: datos.sexo,
    tamano: datos.tamano,
    colorPrincipalPelaje: datos.colorPrincipalPelaje,
    caracteristicasParticulares: sanitizarTexto(datos.caracteristicasParticulares),
    perdido: { lat: datos.latPerdido, lng: datos.lngPerdido },
    perdidoEn: new Date(`${datos.fechaPerdida}T${datos.horaPerdida || "00:00"}`).toISOString(),
    direccionHallazgo: sanitizarTexto(datos.direccionHallazgo),
  };
  // Solo si el geocodificador los devolvio: el schema exige min(1) y una
  // cadena vacia haria fallar la validacion entera.
  if (datos.ciudad) payload.ciudad = datos.ciudad;
  if (datos.departamento) payload.departamento = datos.departamento;
  const puntoReferencia = sanitizarTexto(datos.puntoReferencia);
  if (puntoReferencia) payload.puntoReferencia = puntoReferencia;
  return payload;
}

function SelectorTamano({ tipoMascota, value, onChange }) {
  const [abierto, setAbierto] = useState(false);
  const contenedorRef = useRef(null);
  const opciones = detallesTamano[tipoMascota] ?? detallesTamano.OTRO;
  const seleccionado = value ? opciones[value] : null;

  useEffect(() => {
    function cerrarAlHacerClickFuera(evento) {
      if (!contenedorRef.current?.contains(evento.target)) setAbierto(false);
    }
    document.addEventListener("pointerdown", cerrarAlHacerClickFuera);
    return () => document.removeEventListener("pointerdown", cerrarAlHacerClickFuera);
  }, []);

  return (
    <div className="size-picker" ref={contenedorRef}>
      <button className="form-select" type="button" onClick={() => setAbierto((actual) => !actual)} aria-expanded={abierto} disabled={!tipoMascota}>
        <span>{seleccionado ? `${seleccionado[0]} · ${seleccionado[1]}` : tipoMascota ? "Selecciona el tamaño aproximado" : "Primero selecciona el tipo de mascota"}</span>
        <CaretDownIcon weight="bold" />
      </button>
      {abierto && (
        <div className="size-picker__options" role="listbox" aria-label="Tamaño aproximado">
          {TAMANO_MASCOTA.map((tamano) => {
            const [nombre, peso] = opciones[tamano];
            const activo = value === tamano;
            return (
              <button type="button" className={activo ? "size-option is-selected" : "size-option"} role="option" aria-selected={activo} onClick={() => { onChange(tamano); setAbierto(false); }} key={tamano}>
                <span className="size-option__symbol" aria-hidden="true">{nombre.charAt(0)}</span>
                <span><strong>{nombre}</strong>{peso && <small>{peso}</small>}</span>
                {activo && <CheckIcon weight="bold" />}
              </button>
            );
          })}
          <button type="button" className={!value ? "size-option is-selected" : "size-option"} role="option" aria-selected={!value} onClick={() => { onChange(""); setAbierto(false); }}>
            <span className="size-option__symbol size-option__symbol--unknown" aria-hidden="true"><QuestionIcon weight="bold" /></span>
            <span><strong>No especificado</strong><small>No sé el tamaño aproximado</small></span>
            {!value && <CheckIcon weight="bold" />}
          </button>
        </div>
      )}
      <p className="field-help"><InfoIcon weight="bold" /> Si no conoces el peso exacto, elige el rango que más se aproxime.</p>
    </div>
  );
}

function MascotaPerdidaForm({ onBack }) {
  const [datos, setDatos] = useState(cargarBorrador);
  /// Se resuelve una sola vez, al montar: si el formulario arranco con un
  /// borrador, hay que decirlo. Datos que el usuario no acaba de escribir no
  /// pueden aparecer sin explicacion.
  const [borradorRestaurado, setBorradorRestaurado] = useState(() => hayBorrador(STORAGE_KEY));
  const [advertencia, setAdvertencia] = useState(null);
  const [pasoActual, setPasoActual] = useState(1);
  const [maximoPasoAlcanzado, setMaximoPasoAlcanzado] = useState(1);
  const [fotos, setFotos] = useState([]);
  const [contacto, setContacto] = useState({ nombre: "", codigoPais: "57", telefono: "", email: "" });
  const [publicando, setPublicando] = useState(false);
  const [progreso, setProgreso] = useState(null);
  const [errorEnvio, setErrorEnvio] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [tokenAntiBot, setTokenAntiBot] = useState(null);
  const turnstileRef = useRef(null);
  const fotosRef = useRef(fotos);
  const publicacionRef = useRef({ reporte: null, multimedia: [] });

  useEffect(() => {
    const { latPerdido, lngPerdido, precisionGps, ...borradorPersistente } = datos;
    guardarBorrador(STORAGE_KEY, borradorPersistente);
  }, [datos]);

  useEffect(() => {
    fotosRef.current = fotos;
  }, [fotos]);

  useEffect(() => () => {
    fotosRef.current.forEach((foto) => URL.revokeObjectURL(foto.url));
  }, []);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [pasoActual]);

  function actualizar(campo, valor) {
    const valorSeguro = campo === "nombreMascota" || campo === "caracteristicasParticulares"
      ? sanitizarTexto(valor, { recortar: false })
      : valor;
    setDatos((actuales) => ({ ...actuales, [campo]: valorSeguro }));
  }

  function seleccionarTipo(tipoMascota) {
    setDatos((actuales) => ({ ...actuales, tipoMascota, tamano: "" }));
  }

  function continuar(evento) {
    evento.preventDefault();
    const nombreMascota = sanitizarTexto(datos.nombreMascota);
    const faltantes = [];
    if (!datos.tipoMascota) faltantes.push("Tipo de mascota");
    if (!nombreMascota) faltantes.push("Nombre de la mascota");
    if (!datos.sexo) faltantes.push("Sexo");
    if (!datos.tamano) faltantes.push("Tamaño");
    if (!datos.colorPrincipalPelaje) faltantes.push("Color principal del pelaje");
    if (!sanitizarTexto(datos.caracteristicasParticulares)) faltantes.push("Características particulares");

    setDatos((actuales) => ({ ...actuales, nombreMascota, caracteristicasParticulares: sanitizarTexto(actuales.caracteristicasParticulares) }));
    if (faltantes.length) {
      setAdvertencia(faltantes);
      return;
    }
    avanzarPaso(2);
  }

  function avanzarPaso(paso) {
    setMaximoPasoAlcanzado((actual) => Math.max(actual, paso));
    setPasoActual(paso);
  }

  function descartarBorrador() {
    limpiarBorrador(STORAGE_KEY, STORAGE_KEYS_ANTERIORES);
    setDatos(datosIniciales);
    setBorradorRestaurado(false);
  }

  function cambiarPaso(paso) {
    if (paso <= maximoPasoAlcanzado) setPasoActual(paso);
  }

  function volver() {
    if (pasoActual > 1) cambiarPaso(pasoActual - 1);
    else onBack();
  }

  function validarReporteCompleto() {
    const faltantes = [];
    if (!sanitizarTexto(datos.nombreMascota)) faltantes.push("Nombre de la mascota");
    if (!datos.tipoMascota) faltantes.push("Tipo de mascota");
    if (!datos.sexo) faltantes.push("Sexo");
    if (!datos.tamano) faltantes.push("Tamaño");
    if (!datos.colorPrincipalPelaje) faltantes.push("Color principal del pelaje");
    if (!sanitizarTexto(datos.caracteristicasParticulares)) faltantes.push("Características particulares");
    if (datos.latPerdido == null || datos.lngPerdido == null) faltantes.push("Ubicación exacta en el mapa");
    if (!sanitizarTexto(datos.direccionHallazgo)) faltantes.push("Dirección o referencia donde se perdió");
    if (!datos.fechaPerdida || !datos.horaPerdida) faltantes.push("Fecha y hora aproximada");
    if (!fotos.length) faltantes.push("Al menos una foto");
    if (!sanitizarTexto(contacto.nombre)) faltantes.push("Nombre completo");
    if (contacto.telefono.length !== 10) faltantes.push("WhatsApp de 10 dígitos");
    if (!sanitizarEmail(contacto.email)) faltantes.push("Correo electrónico");
    if (faltantes.length) {
      setAdvertencia(faltantes);
      return null;
    }

    const validacion = crearMascotaSchema.safeParse(construirPayload(datos, contacto));
    if (!validacion.success) {
      const errores = validacion.error.issues.map((problema) => {
        const campo = problema.path.join(".");
        return campo ? `${campo}: ${problema.message}` : problema.message;
      });
      setAdvertencia([...new Set(errores)]);
      return null;
    }
    return validacion.data;
  }

  async function publicar() {
    if (publicando) return;
    const payload = validarReporteCompleto();
    if (!payload) return;
    setPublicando(true);
    setErrorEnvio(null);
    setProgreso({ indice: 0, total: fotos.length, porcentaje: 0 });
    try {
      const reporte = await publicarMascota({ payload, fotos, estado: publicacionRef.current, onProgreso: setProgreso, tokenAntiBot });
      limpiarBorrador(STORAGE_KEY, STORAGE_KEYS_ANTERIORES);
      setResultado(reporte);
    } catch (error) {
      setErrorEnvio(error);
      // El token de Turnstile se gasta en cada intento: hay que pedir uno nuevo
      // o el reintento vuelve a fallar con 403.
      setTokenAntiBot(null);
      turnstileRef.current?.reiniciar();
    } finally {
      setPublicando(false);
    }
  }

  if (resultado) {
    return (
      <div className="report-flow">
        <ReportePublicado variante="perdida" resultado={resultado} datos={datos} fotos={fotos} contacto={contacto} onInicio={() => { limpiarBorrador(STORAGE_KEY, STORAGE_KEYS_ANTERIORES); onBack(); }} />
      </div>
    );
  }

  return (
    <div className="report-flow lost-pet-flow">
      <header className="report-flow__header">
        <button type="button" onClick={volver} aria-label={pasoActual === 1 ? "Volver al inicio" : "Volver a información"}><ArrowLeftIcon weight="bold" /></button>
        <h1>Reportar mascota perdida</h1>
      </header>

      <ol className="stepper stepper--lost" aria-label="Progreso del reporte">
        {pasos.map((paso, indice) => (
          <li className={clasePaso(indice + 1, pasoActual, maximoPasoAlcanzado)} aria-current={indice + 1 === pasoActual ? "step" : undefined} key={paso}>
            <button type="button" disabled={indice + 1 > maximoPasoAlcanzado} onClick={() => cambiarPaso(indice + 1)} aria-label={`Paso ${indice + 1}: ${paso}`}>{indice + 1 < pasoActual ? <CheckIcon weight="bold" /> : indice + 1}</button>
            <strong>{paso}</strong>
          </li>
        ))}
      </ol>

      {pasoActual === 1 ? <><div className="lost-pet-intro">
        <span><HeartIcon weight="fill" /><PawPrintIcon weight="fill" /></span>
        <h2>Cuéntanos sobre<br />tu mascota</h2>
        <p>Mientras más información brindes, más rápido podremos ayudar a encontrarla.</p>
      </div>

      {borradorRestaurado && <AvisoBorrador onDescartar={descartarBorrador} />}
      <form className="lost-pet-form" onSubmit={continuar}>
        <fieldset className="pet-type">
          <legend>Tipo de mascota</legend>
          <div className="lost-pet-types">
            {TIPO_MASCOTA.map((tipo) => {
              const Icon = iconosTipo[tipo];
              return (
                <button className={datos.tipoMascota === tipo ? "is-selected" : ""} type="button" aria-pressed={datos.tipoMascota === tipo} onClick={() => seleccionarTipo(tipo)} key={tipo}>
                  <Icon weight="bold" /><span>{etiquetasTipo[tipo]}</span>
                </button>
              );
            })}
          </div>
        </fieldset>

        <label>Nombre de tu mascota <b aria-hidden="true">*</b>
          <input type="text" autoComplete="off" maxLength="60" value={datos.nombreMascota} onChange={(evento) => actualizar("nombreMascota", evento.target.value)} placeholder="Ej: Rocky" required />
        </label>

        <fieldset className="basic-fields lost-basic-fields">
          <legend>Descripción básica</legend>
          <label>Sexo
            <span className="select-wrap"><select value={datos.sexo} onChange={(evento) => actualizar("sexo", evento.target.value)}><option value="">Selecciona</option>{SEXO_MASCOTA.map((sexo) => <option value={sexo} key={sexo}>{etiquetasSexo[sexo]}</option>)}</select><CaretDownIcon weight="bold" /></span>
          </label>
          <label>Tamaño</label>
          <SelectorTamano tipoMascota={datos.tipoMascota} value={datos.tamano} onChange={(valor) => actualizar("tamano", valor)} />
          <label>Color principal del pelaje
            <span className="select-wrap"><select value={datos.colorPrincipalPelaje} onChange={(evento) => actualizar("colorPrincipalPelaje", evento.target.value)}><option value="">Selecciona</option>{COLOR_PELAJE.map((color) => <option value={color} key={color}>{etiquetasColor[color]}</option>)}</select><CaretDownIcon weight="bold" /></span>
          </label>
          <label>Características particulares
            <textarea maxLength="500" rows="4" value={datos.caracteristicasParticulares} onChange={(evento) => actualizar("caracteristicasParticulares", evento.target.value)} placeholder="Ej: tiene una mancha blanca en el pecho, collar azul, cola rizada…" />
            <small className="character-count">{datos.caracteristicasParticulares.length}/500</small>
          </label>
        </fieldset>

        <button className="continue-button" type="submit">Siguiente <ArrowRightIcon weight="bold" /></button>
      </form></> : pasoActual === 2 ? (
        <UbicacionMascotaForm
          variante="perdida"
          datos={datos}
          onChange={setDatos}
          onBack={() => cambiarPaso(1)}
          onNext={() => avanzarPaso(3)}
          onMissing={setAdvertencia}
        />
      ) : pasoActual === 3 ? (
        <FotosMascotaForm variante="perdida" fotos={fotos} onChange={setFotos} onBack={() => cambiarPaso(2)} onNext={() => avanzarPaso(4)} onMissing={setAdvertencia} />
      ) : (
        <ConfirmarMascotaForm variante="perdida" datos={datos} fotos={fotos} contacto={contacto} onContactChange={setContacto} onBack={() => cambiarPaso(3)} onEditStep={cambiarPaso} onPublicar={publicar} widgetAntiBot={<Turnstile ref={turnstileRef} onToken={setTokenAntiBot} onError={(m) => setErrorEnvio(new Error(m))} onExpirado={() => setTokenAntiBot(null)} />} publicando={publicando} progreso={progreso} errorEnvio={errorEnvio} />
      )}

      <nav className="flow-nav" aria-label="Navegación principal">
        <button type="button" onClick={onBack}><HouseIcon weight="fill" /><span>Inicio</span></button>
        <span  className="nav-pendiente" aria-disabled="true" title="Disponible proximamente"><ListIcon /><span>Reportes</span></span>
        <a className="is-active" href="#reportar"><span className="flow-nav__add"><PlusIcon weight="bold" /></span><span>Reportar</span></a>
        <span  className="nav-pendiente" aria-disabled="true" title="Disponible proximamente"><BellIcon /><span>Alertas</span></span>
        <span  className="nav-pendiente" aria-disabled="true" title="Disponible proximamente"><UserCircleIcon /><span>Mi cuenta</span></span>
      </nav>

      {advertencia && <ModalCamposFaltantes campos={advertencia} onClose={() => setAdvertencia(null)} onReview={() => setAdvertencia(null)} />}
    </div>
  );
}

export default MascotaPerdidaForm;
